import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApps } from "../../../hooks/useData";
import Api from "../../../services/api";
import { toast } from "react-toastify";
import { Button } from "react-bootstrap";
import { Row, Col, Drawer, Button as AntButton, Input as AntInput, Select as AntSelect, Radio, Modal, Form, Switch, DatePicker, message } from "antd";
import { SearchOutlined, AndroidFilled, AppleFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import Cookies from "js-cookie";
import { Plus } from "react-bootstrap-icons";
import NoDataComponent from "../../../components/noData";
import { Flipper, Flipped } from "react-flip-toolkit";
import "../../../assets/css/app/flip-animation.css";

function Apps() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { apps, loading: appsLoading, refetch: refetchApps } = useApps();
  const [show, setShow] = useState(false);
  const [studios, setStudios] = useState([]);
  const [loadingStudios, setLoadingStudios] = useState(false);

  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterOs, setFilterOs] = useState("");
  const [appFilter, setAppFilter] = useState([]);

  
  const [sortBy, setSortBy] = useState("default"); 
  const [sortDirection, setSortDirection] = useState("desc");  

  const originalApps = useRef([]);
  const [initialCounts, setInitialCounts] = useState({
    all: 0,
    running: 0,
    off: 0
  });

  const [formData, setFormData] = useState({
    name: "",
    code: "",
    studio_id: "",
    app_store_id: "",
    thumb: "",
    status: "off",
    release_at: "",
    dataset: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [form] = Form.useForm();

  
  const debounce = (func, delay) => {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => func.apply(this, args), delay);
    };
  };

  
  const sortApps = useCallback((appsToSort) => {
    if (!appsToSort) return [];

    if (sortBy === "default") {
      return appsToSort;
    }
    
    return [...appsToSort].sort((a, b) => {
      if (sortBy === "name") {
        
        const nameA = a.name?.toLowerCase() || '';
        const nameB = b.name?.toLowerCase() || '';
        return sortDirection === "asc" 
          ? nameA.localeCompare(nameB) 
          : nameB.localeCompare(nameA);
      } else {
        
        const dateA = new Date(a.release_at || 0);
        const dateB = new Date(b.release_at || 0);
        return sortDirection === "desc" 
          ? dateB - dateA 
          : dateA - dateB;
      }
    });
  }, [sortBy, sortDirection]);

  
  useEffect(() => {
    if (apps) {
      originalApps.current = apps;
      const runningItems = apps.filter((item) => item.status === "running");
      const offItems = apps.filter((item) => item.status === "off");
      
      setInitialCounts({
        all: apps.length,
        running: runningItems.length,
        off: offItems.length
      });

      let filtered = apps;
      if (filterStatus === "running") {
        filtered = runningItems;
      } else if (filterStatus === "off") {
        filtered = offItems;
      }

      if (filterName) {
        const searchTerm = filterName.toLowerCase();
        filtered = filtered.filter(
          (item) =>
            item.name.toLowerCase().includes(searchTerm) ||
            item.code.toLowerCase().includes(searchTerm)
        );
      }

      if (filterOs) {
        filtered = filtered.filter((item) => item.os === filterOs);
      }

      
      filtered = sortApps(filtered);

      setAppFilter(filtered);
      updateFilteredApps();
    }
  }, [apps, filterStatus, filterName, filterOs, sortBy, sortDirection, sortApps]);

  
  useEffect(() => {
    if (Cookies.get(process.env.REACT_APP_AUTH_KEY)) {
      const fetchStudios = async () => {
        setLoadingStudios(true);
        try {
          const response = await Api.get("/api/studios");
          if (response?.data?.data) {
            setStudios(response.data.data || []);
          }
        } catch (error) {
          console.error("Error fetching studios:", error);
          toast.error("Failed to load studios");
        } finally {
          setLoadingStudios(false);
        }
      };

      fetchStudios();
    }
  }, [Cookies.get(process.env.REACT_APP_AUTH_KEY)]);

  
  useEffect(() => {
    const name = searchParams.get("name") || "";
    const status = searchParams.get("status") || "";
    const os = searchParams.get("os") || "";

    setFilterName(name);
    setFilterStatus(status);
    setFilterOs(os);

    refetchApps({ name, status, os });
  }, [refetchApps, searchParams]);

  const handleReset = () => {
    setFilterName("");
    setFilterStatus("");
    setFilterOs("");
    setSearchParams({});
    refetchApps();
  };

  const updateFilteredApps = useCallback(() => {
    if (!originalApps.current) return;

    let filtered = [...originalApps.current];
    
    
    if (filterStatus === "running") {
      filtered = filtered.filter(item => item.status === "running");
    } else if (filterStatus === "off") {
      filtered = filtered.filter(item => item.status === "off");
    }

    
    if (filterName) {
      const searchTerm = filterName.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm) ||
          item.code.toLowerCase().includes(searchTerm)
      );
    }

    
    if (filterOs) {
      filtered = filtered.filter((item) => item.os === filterOs);
    }

    
    filtered = sortApps(filtered);

    setAppFilter(filtered);
  }, [filterStatus, filterName, filterOs, sortApps]);

  
  useEffect(() => {
    updateFilteredApps();
  }, [updateFilteredApps]);

  const handleLoading = useCallback(
    debounce((newFilters = {}) => {
      const params = {
        name: newFilters.name || filterName || "",
        status: newFilters.status || filterStatus || "",
        os: newFilters.os || filterOs || "",
      };

      setSearchParams(params);
      refetchApps(params);
    }, 500),
    [filterName, filterStatus, filterOs]
  );

  
  const handleSortChange = (type) => {
    if (type === "name") {
      setSortBy("name");
      setSortDirection("asc"); 
    } else if (type === "release_at") {
      setSortBy("release_at");
      setSortDirection("desc"); 
    } else if (type === "default") {
      setSortBy("default");
      setSortDirection("desc");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsSubmitting(true);

      const submitData = {
        ...values,
        status: values.status ? "running" : "off",
        release_at: values.release_at ? values.release_at.format("YYYY-MM-DD") : "",
      };

      const request = formData.id
        ? Api.put(`/api/app/${formData.id}`, {
            ...submitData,
          })
        : Api.post(`/api/app`, {
            code: submitData.code,
            app_store_id: submitData.app_store_id,
            studio_id: submitData.studio_id,
          });
      const response = await request;
      if (response.data.code === 0) {
        toast.success(response.data.message || "App created successfully");

        // Refresh apps data after creating new app
        try {
          await refetchApps();
        } catch (fetchError) {
          console.error("Error fetching apps:", fetchError);
        }
        setShow(false);
        resetForm();
      } else {
        throw new Error(response.data.message || "Unknown error occurred");
      }
    } catch (error) {
      if (error.errorFields) {
        // Validation errors from antd form
        return;
      }
      console.error("Error creating app:", error);
      toast.error(error.message || "Error creating app");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChangeName = (e) => {
    setFilterName(e);
  };

  const handleChangeStatus = (value) => {
    setFilterStatus(value);
    
    updateFilteredApps();
  };

  const handleChangeOs = (value) => {
    setFilterOs(value || "");
  };

  const selectApp = (item) => {
    // No need to set app in Redux anymore, just navigate
    navigate(`/apps/${item.code}/${item.os}/overview`);
  };

  const editApp = (item) => {
    const editData = {
      id: item.id,
      name: item.name,
      code: item.code,
      studio_id: item.studio_id || item.merchant_id || "",
      app_store_id: "",
      thumb: item.thumb,
      status: item.status,
      release_at: item.release_at,
      dataset: item.bigquery_dataset_name,
    };
    setFormData(editData);
    
    // Set form values for antd form
    form.setFieldsValue({
      name: editData.name,
      code: editData.code,
      studio_id: editData.studio_id || undefined,
      app_store_id: editData.app_store_id || undefined,
      thumb: editData.thumb || undefined,
      status: editData.status === "running",
      release_at: editData.release_at ? dayjs(editData.release_at) : undefined,
      dataset: editData.dataset || undefined,
    });
    setShow(true);
  };

  const resetForm = () => {
    form.resetFields();
    setFormData({
      name: "",
      code: "",
      studio_id: "",
      app_store_id: "",
      thumb: "",
      status: "off",
      release_at: "",
      dataset: "",
    });
    setErrors({});
  };

  const resetApp = () => {
    form.resetFields();
    setFormData({
      name: "",
      code: "",
      studio_id: "",
      app_store_id: "",
      thumb: "",
      status: "off",
      release_at: "",
      dataset: "",
    });
  };

  return (
    <>
      <style>{`
        /* Responsive styles for Apps filter */
        @media (max-width: 768px) {
          .desktop-filter { display: none !important; }
          .mobile-search-toggle { display: block !important; }
          .filter-container .sort-container {
            order: 3;
          }
          .filter-container .status-container {
            order: 2;
          }
          .status-filters {
            flex-wrap: wrap;
            gap: 0.5rem;
          }
          .status-filters > div {
            margin-right: 0.75rem !important;
            margin-bottom: 0.5rem;
          }
          .reset-btn-mobile { display: none !important; }
          .sort-label { display: none; }
        }
        @media (min-width: 769px) { .reset-btn-mobile { display: none !important; } .mobile-search-toggle { display: none !important; } }
        @media (min-width: 769px) {
          .status-container .status-filters { justify-content: flex-end; }
        }
        .list_app {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
        }
        @media (max-width: 576px) {
          .list_app {
            grid-template-columns: 1fr;
          }
        }
        /* Responsive styles for Modal */
        @media (max-width: 768px) {
          .app-form-modal.ant-modal {
            margin: 0;
            max-width: 100% !important;
            top: 0;
            padding-bottom: 0;
          }
          .app-form-modal .ant-modal-content {
            border-radius: 0;
            height: 100vh;
            display: flex;
            flex-direction: column;
          }
          .app-form-modal .ant-modal-body {
            flex: 1;
            overflow-y: auto;
            padding: 16px;
          }
          .app-form-modal .ant-modal-footer {
            padding: 12px 16px;
            border-top: 1px solid #f0f0f0;
          }
        }
      `}</style>
      <div className="background-second-dashboard">
        {/* Mobile: floating search icon */}
        { !drawerOpen && (
          <div className="mobile-search-toggle" style={{
            display: 'none', position: 'fixed', right: '16px', top: '54px', zIndex: 1002
          }}>
            <AntButton type="primary" onClick={() => setDrawerOpen(true)}
              icon={<SearchOutlined />}
              style={{ borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.15)'}} />
          </div>
        )}
        <section className="main__content-dashbard">
          <div className="container-fluid">
            <div className="detail-body">
              <div className="overview_content pd-20">
                <Button
                  variant="success"
                  className="floating-btn"
                  onClick={() => {
                    resetApp();
                    form.setFieldsValue({
                      name: "",
                      code: "",
                      studio_id: undefined,
                      app_store_id: "",
                      thumb: undefined,
                      status: false,
                      release_at: undefined,
                      dataset: undefined,
                    });
                    setShow(true);
                  }}
                >
                  <Plus size={30} />
                </Button>

                <div className="card mb-4 desktop-filter" style={{ 
                  position: 'sticky', 
                  top: '46px', 
                  zIndex: 9,
                  backgroundColor: '#fff',
                  boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                }}>
                  <div className="card-body" style={{ padding: "1.25rem" }}>
                    <div className="filter-container">
                      <Row gutter={[16, 16]} className="align-items-center">
                        <Col xs={24} md={6}>
                          <AntInput
                            placeholder="App name, code"
                            value={filterName || ""}
                            onChange={(e) => handleChangeName(e.target.value)}
                          />
                        </Col>
                        <Col xs={12} md={3}>
                          <AntSelect
                            value={filterOs || undefined}
                            onChange={handleChangeOs}
                            allowClear
                            placeholder="All Os"
                            style={{ width: '100%' }}
                            options={[
                              { 
                                value: 'android', 
                                label: (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <AndroidFilled /> Android
                                  </span>
                                )
                              },
                              { 
                                value: 'ios', 
                                label: (
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <AppleFilled /> iOS
                                  </span>
                                )
                              }
                            ]}
                          />
                        </Col>
                        <Col xs={24} md={8} className="sort-container">
                          <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.5rem' }}>
                            <span className="me-3 sort-label">Sort by</span>
                            <Radio.Group
                              value={sortBy}
                              onChange={(e) => handleSortChange(e.target.value)}
                              style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}
                            >
                              <Radio value="name">Name</Radio>
                              <Radio value="default">Revenue</Radio>
                              <Radio value="release_at">Release</Radio>
                            </Radio.Group>
                          </div>
                        </Col>
                        <Col xs={24} md={7} className="status-container">
                          <div className="d-flex status-filters align-items-center">
                            <div
                              className={`me-4 lh-lg ${
                                filterStatus === ""
                                  ? "title-block-active"
                                  : "title-block"
                              }`}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleChangeStatus("")}
                            >
                              All ({initialCounts.all ?? 0})
                            </div>
                            <div
                              className={`me-4 lh-lg ${
                                filterStatus === "running"
                                  ? "title-block-active"
                                  : "title-block"
                              }`}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleChangeStatus("running")}
                            >
                              Running ({initialCounts.running ?? 0})
                            </div>
                            <div
                              className={`lh-lg ${
                                filterStatus === "off"
                                  ? "title-block-active"
                                  : "title-block"
                              }`}
                              style={{ cursor: "pointer" }}
                              onClick={() => handleChangeStatus("off")}
                            >
                              Off ({initialCounts.off ?? 0})
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <Flipper 
                    flipKey={`${sortBy}-${sortDirection}-${appFilter.length}`}
                    spring={{
                      type: "spring",
                      stiffness: 300,
                      damping: 25
                    }}
                    staggerConfig={{
                      default: {
                        reverse: false,
                        speed: 0.8
                      }
                    }}
                  >
                    {appFilter.length > 0 ? (
                      <div className="list_app" style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                        gap: '1rem',
                        justifyContent: 'center'
                      }}>
                        {appFilter.map((i, k) => {
                          const uniqueKey = `app-${i.id || i.code}-${i.os}`;
                          return (
                            <Flipped 
                              key={uniqueKey} 
                              flipId={uniqueKey}
                              stagger={true}
                              transformOrigin="center center"
                            >
                              <div>
                                <div className="card">
                                  <div
                                    className={`card-body d-flex align-items-center ${
                                      i.status === "off" ? "app-off" : ""
                                    }`}
                                    style={{ padding: "1.25rem" }}
                                  >
                                    <div className="app-img me-3">
                                      <img
                                        src={i.thumb}
                                        alt={i.name}
                                        onClick={() => selectApp(i)}
                                      />
                                      <img
                                        alt={``}
                                        className="app-logo"
                                        src={`/bg-${i.os}.png`}
                                        onClick={() => selectApp(i)}
                                      />
                                    </div>
                                    <div className="app-title">
                                      <h6
                                        style={{ color: "black" }}
                                        onClick={() => selectApp(i)}
                                      >
                                        {i.code} - {i.name}
                                      </h6>
                                      Status:{" "}
                                      <span
                                        className="mr-2 text-uppercase"
                                        style={{ color: "#29A1DD" }}
                                      >
                                        {i.status}
                                      </span>
                                      OB:{" "}
                                      <span style={{ color: "#29A1DD" }}>
                                        {i.release_at}
                                      </span>
                                    </div>
                                    <i
                                      className="fa fa-edit"
                                      onClick={() => editApp(i)}
                                    ></i>
                                  </div>
                                </div>
                              </div>
                            </Flipped>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="col-12 text-center py-5">
                        <NoDataComponent />
                      </div>
                    )}
                  </Flipper>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Mobile Drawer for filters */}
      <Drawer
        title="Search Filters"
        placement="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={320}
        zIndex={2500}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <AntInput
            placeholder="App name, code"
            value={filterName || ''}
            onChange={(e) => setFilterName(e.target.value)}
          />
          <AntSelect
            placeholder="Sort by"
            value={sortBy}
            onChange={(val) => handleSortChange(val)}
            options={[
              { value: 'default', label: 'Revenue' },
              { value: 'name', label: 'Name' },
              { value: 'release_at', label: 'Release' },
            ]}
          />
          <AntSelect
            allowClear
            placeholder="All OS"
            value={filterOs || undefined}
            onChange={(val) => setFilterOs(val || '')}
            options={[
              { 
                value: 'android', 
                label: (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AndroidFilled /> Android
                  </span>
                )
              },
              { 
                value: 'ios', 
                label: (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <AppleFilled /> iOS
                  </span>
                )
              }
            ]}
          />
          <AntSelect
            allowClear
            placeholder="All Status"
            value={filterStatus || undefined}
            onChange={(val) => setFilterStatus(val || '')}
            options={[
              { value: '', label: 'All' },
              { value: 'running', label: 'Running' },
              { value: 'off', label: 'Off' }
            ]}
          />
        </div>
      </Drawer>

      <Modal
        title={formData.id ? "Edit App" : "New App"}
        open={show}
        onCancel={() => {
          setShow(false);
          resetForm();
        }}
        footer={[
          <AntButton
            key="cancel"
            onClick={() => {
              setShow(false);
              resetForm();
            }}
          >
            Cancel
          </AntButton>,
          <AntButton
            key="submit"
            type="primary"
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            {formData.id ? "Update App" : "Add App"}
          </AntButton>,
        ]}
        width="90%"
        style={{ maxWidth: "800px" }}
        destroyOnClose
        className="app-form-modal"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: false,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: "Name is required" }]}
              >
                <AntInput placeholder="Enter app name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Code"
                name="code"
                rules={[{ required: true, message: "Code is required" }]}
              >
                <AntInput placeholder="Enter app code" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Studio" name="studio_id">
                <AntSelect
                  placeholder={loadingStudios ? "Loading studios..." : "Select a studio"}
                  disabled={loadingStudios}
                  allowClear
                >
                  {studios.map((studio) => (
                    <AntSelect.Option key={studio.code} value={studio.id}>
                      {studio.name} ({studio.code})
                    </AntSelect.Option>
                  ))}
                </AntSelect>
              </Form.Item>
            </Col>
            {!("id" in formData) && (
              <Col xs={24} sm={12}>
                <Form.Item
                  label="App store ID"
                  name="app_store_id"
                  rules={[
                    {
                      required: true,
                      message: "App store ID is required",
                    },
                  ]}
                >
                  <AntInput placeholder="Enter app store ID" />
                </Form.Item>
              </Col>
            )}
            {formData.id && (
              <>
                <Col xs={24} sm={12}>
                  <Form.Item label="Thumb" name="thumb">
                    <AntInput placeholder="Enter thumb URL" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Release At" name="release_at">
                    <DatePicker
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      placeholder="Select release date"
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item label="Bigquery Dataset" name="dataset">
                    <AntInput placeholder="Enter Bigquery dataset" />
                  </Form.Item>
                </Col>
              </>
            )}
            <Col xs={24} sm={12}>
              <Form.Item label="Status" name="status" valuePropName="checked">
                <Switch
                  checkedChildren="Running"
                  unCheckedChildren="Off"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default Apps;